//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import Media from '@components/atoms/media/Media';
import { getContentLibraryItemMediaType } from '@components/atoms/media/getMediaType';
import MediaCard from '@components/molecules/media-card/MediaCard';
import { useBoundActionCreators } from '@hooks/useCuralateStore';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import {
  getUniversalImageActionsView,
  getContentItemDetailsService,
} from '@react-bridge/wrappers/ng-service-provider';
import { getClientId } from '@redux/selectors/getClientId';
import { actions as streamDrawerActions } from '@streams/actions/streamDrawerActions';
import { openUiaToView } from '@streams-drawer/streams-drawer-content/streams-drawer-moderation-content/streamsDrawerModerationUtils';

import { IContentItemDetails } from 'app/bulk-actions/model/AppState';
import { CAPTIONS_STATUS_VALUE } from 'app/constants/mute-tooltip-content';

interface IOwnProps {
  item: IContentLibraryItem;
  disableBulkActions: boolean;
}

type Props = IOwnProps;

const StreamsDrawerMediaCard: React.FunctionComponent<Props> = ({
  item,
  disableBulkActions,
}) => {
  const { updateItemWithUiaUpdates } =
    useBoundActionCreators<typeof streamDrawerActions>(streamDrawerActions);
  const UniversalImageActionsView = getUniversalImageActionsView();

  const [itemDetails, setItemDetails] = useState<
    IContentItemDetails | undefined
  >();
  const captionSelectedItems = useSelector(
    (state) => state.bulkActions.selectedItemsForCaptions,
  );
  const captionTurnOnOffState = useSelector(
    (state) => state.bulkActions.captionTurnOnOffState,
  );
  const clientId = useSelector(getClientId);

  const cuid = Cuid.build({ itemType: item.itemType, id: item.id }, clientId);
  const captionStatusValue: string | undefined = captionSelectedItems[cuid];

  const isCaptionInProgress =
    captionTurnOnOffState === 'on' &&
    captionStatusValue === CAPTIONS_STATUS_VALUE.IN_PROGRESS;

  const isCaptioned =
    captionTurnOnOffState === 'on' &&
    captionStatusValue === CAPTIONS_STATUS_VALUE.SUCCEEDED;
  useEffect(() => {
    if (isCaptioned) {
      setItemDetails((prevItemDetails: any) => ({
        ...prevItemDetails,
        captionStatus: true,
      }));
    }
  }, [isCaptioned]);

  // Whenever the item changes, we'll need to hydrate it with some
  // additional metadata. This hook fetches item details and stores
  // them as state.
  useEffect(() => {
    if (item) {
      const contentLibraryItemDetailsService = getContentItemDetailsService();
      const partialCuid = PartialCuid.build(item);
      const itemDetailsStream =
        contentLibraryItemDetailsService.requestItemDetails(partialCuid, false);
      itemDetailsStream.subscribe((itemDetailsMap) => {
        const itemDetailsObj = itemDetailsMap?.[partialCuid];
        if (itemDetailsObj) {
          setItemDetails(itemDetailsObj);
        }
      });
    }
  }, [item]);

  return (
    <StreamMediaCard
      itemType={item.itemType}
      id={item.id}
      image={
        <StreamMedia
          metadataLayer={{
            isCaptionInProgress: isCaptionInProgress,
            enabled: true,
            itemDetails: itemDetails,
            mediaType: getContentLibraryItemMediaType(item),
          }}
          bulkSelectionLayer={{
            enabled: !disableBulkActions,
          }}
          imageUrl={item.imageUrl}
          itemType={item.itemType}
          id={item.id}
          onItemClick={(e) => {
            openUiaToView(
              e,
              UniversalImageActionsView.Details.name,
              item.itemType,
              item.id,
              updateItemWithUiaUpdates,
            );
          }}
        />
      }
    />
  );
};

const StreamMediaCard = styled(MediaCard)`
  height: 220px;
  width: 220px;
`;

const StreamMedia = styled(Media)`
  height: 220px;
  width: 220px;
`;

export default StreamsDrawerMediaCard;
